import React, { useState, useEffect, useRef, useCallback } from "react";
import { useSelector } from "react-redux";
import { useFetch } from "services/hooks/useFetch";
import { Loader } from "components/Loader";
import { CustomFilter } from "components/CustomComponents/CustomFilter";
import { InfoMessage } from "components/CustomComponents/CustomButton";
import {
  themes,
  scrollTop,
  stickyHeader,
  tableRowSize,
  StatusBadge,
  drawerStyle,
} from "services/constants";
import {
  Typography,
  Box,
  Paper,
  Grid,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableContainer,
  TablePagination,
  Drawer,
  IconButton,
  Avatar,
  CircularProgress, // <-- Added for the localized custom loader
} from "@mui/material";
import ArrowRightIcon from "@mui/icons-material/ArrowRight";
import { FilterProvider } from "pages/FilterContext";
import TeamAdvanceSalaryPanel from "./TeamAdvanceSalaryPanel"; // Removed unused buildStatusHistory/CustomTimeline
import {
  DateRange,
  Person,
  ModeStandby,
  CurrencyExchange,
} from "@mui/icons-material";
import { customGetAPI } from "services/utils/CustomAPI";
import { LoaderWithoutHeight } from "components/Loader";

// 1. Added "Employee Name" to the headers for Team View
const teamAdvanceSalaryHeader = [
  {
    key: "user_name",
    name: "Employee Name",
    align: "left",
    type: "string",
    icon: <Person fontSize="small" />,
  },
  {
    key: "employee_id",
    name: "Employee ID",
    align: "left",
    type: "string",
    icon: <Person fontSize="small" />,
  },
  {
    key: "department",
    name: "Department",
    align: "left",
    type: "string",
    icon: <Person fontSize="small" />,
  },
  {
    key: "disbursement_date",
    name: "Disbursement Date",
    align: "left",
    type: "date",
    icon: <DateRange fontSize="small" />,
    showBetweenOperator: true,
    dateRangeFields: {
      fromField: "ot_date",
      toField: "ot_date",
    },
  },
  {
    key: "effective_amount",
    name: "Amount",
    align: "left",
    type: "number",
    icon: <CurrencyExchange fontSize="small" />,
  },
  {
    key: "paid_amount",
    name: "Paid Amount",
    align: "left",
    type: "number",
    icon: <CurrencyExchange fontSize="small" />,
  },
  {
    key: "balance_amount",
    name: "Balance",
    align: "left",
    type: "number",
    icon: <CurrencyExchange fontSize="small" />,
  },
  {
    key: "status",
    name: "Status",
    align: "left",
    type: "string",
    icon: <ModeStandby fontSize="small" />,
  },
];

const listHeader = [
  { key: "expand", name: "", align: "center" },
  ...teamAdvanceSalaryHeader,
];

// --- New Component for Expanded Table ---
const ExpandedScheduleTable = ({ domain, advanceId }) => {
  const [schedule, setSchedule] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchSchedule = async () => {
      try {
        setLoading(true);
        // Uses the provided domain + your specific endpoint

        const response = await customGetAPI(
          `${domain}advance/repayment/${advanceId}/schedule/`,
        );
        const result = response.responseDetails || response.data || response;
        if (result.success && result.data) {
          setSchedule(result.data.schedule || []);
        }
      } catch (error) {
        console.error("Failed to fetch schedule", error);
      } finally {
        setLoading(false);
      }
    };

    if (advanceId) {
      fetchSchedule();
    }
  }, [domain, advanceId]);

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
        <LoaderWithoutHeight />
      </Box>
    );
  }

  return (
    <TableContainer
      component={Paper}
      elevation={0}
      sx={{ border: "1px solid #e0e0e0", my: 2, borderRadius: 2 }}
    >
      <Table size="small">
        <TableHead>
          <TableRow sx={{ bgcolor: "#f1f1f1" }}>
            <TableCell sx={{ fontWeight: 600 }}>Repayment Due Date</TableCell>
            <TableCell sx={{ fontWeight: 600 }}>Payrun</TableCell>
            <TableCell sx={{ fontWeight: 600 }}>Paid Amount</TableCell>
            <TableCell sx={{ fontWeight: 600 }}>Balance</TableCell>
            <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {schedule.length > 0 ? (
            schedule.map((item) => (
              <TableRow key={item.id}>
                <TableCell>{item.due_date || "-"}</TableCell>
                <TableCell>{item.payrun_label || "N/A"}</TableCell>
                <TableCell>
                  {item.amount_paid !== null ? item.amount_paid : "0.00"}
                </TableCell>
                <TableCell>
                  {item.balance !== null ? item.balance : "0.00"}
                </TableCell>
                <TableCell>{item.status || "-"}</TableCell>
              </TableRow>
            ))
          ) : (
            <TableRow>
              <TableCell colSpan={5} align="center">
                No schedule available.
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );
};
// ----------------------------------------

const TeamAdvanceSalaryContent = () => {
  const { domain, userDetails } = useSelector((state) => state.tokenReducer);

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [filteredAdvances, setFilteredAdvances] = useState([]);

  const [expandedIndex, setExpandedIndex] = useState(null);
  const [expandedData, setExpandedData] = useState(null);

  const [isPanelOpen, setIsPanelOpen] = useState(false);
  const [panelMode, setPanelMode] = useState("view");
  const [selectedAdvance, setSelectedAdvance] = useState(null);

  const containerRef = useRef(null);

  const {
    result: advanceData,
    setLoad: reCallApi,
    loading,
  } = useFetch(
    `${domain}advance/request/?user_id=${userDetails?.id}&exclude_status=Rejected`,
  );

  useEffect(() => {
    const records = advanceData?.data || [];
    setFilteredAdvances(records);
  }, [advanceData]);

  useEffect(() => {
    if (userDetails?.id) reCallApi(true);
  }, [reCallApi, userDetails]);

  const handleChangePage = (event, newPage) => setPage(newPage);
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleFilteredDataChange = useCallback(
    (filteredData) => {
      setFilteredAdvances(filteredData);
      if (page * rowsPerPage >= filteredData.length) setPage(0);
    },
    [page, rowsPerPage],
  );

  const handleToggle = (e, index, value) => {
    e.stopPropagation();
    setExpandedIndex((prev) => (prev === index ? null : index));
    setExpandedData(value);
  };

  const handleView = (row) => {
    setPanelMode("view");
    setSelectedAdvance(row);
    setIsPanelOpen(true);
  };

  const handleAction = () => {
    setPanelMode("action");
  };

  const handlePanelClose = () => {
    setIsPanelOpen(false);
    setTimeout(() => setSelectedAdvance(null), 300);
  };

  useEffect(() => {
    scrollTop(containerRef);
  }, [page]);

  if (loading) return <Loader />;

  return (
    <Box>
      <Box sx={{ mb: 2 }}>
        <Typography variant="h5" sx={{ fontWeight: 600, mb: 1 }}>
          Advance
        </Typography>
        <Typography variant="body2" color="text.secondary">
          The Incentive Payout Processing screen is used to calculate, review,
          approve, and complete employee incentive payouts as part of the
          payroll process.
        </Typography>
      </Box>
      <Paper sx={{ width: "100%", height: "78vh" }}>
        <CustomFilter
          title={"Team Advance Records"}
          filterHeaders={listHeader}
          data={advanceData?.data || []}
          filteredData={filteredAdvances}
          onFilteredDataChange={handleFilteredDataChange}
          screenAccess={true}
        />

        <Grid sx={{ ml: 2, mr: 2 }}>
          <TableContainer ref={containerRef} sx={{ maxHeight: 590 }}>
            <Table>
              <TableHead sx={{ ...stickyHeader, bgcolor: "#f5f5f5" }}>
                <TableRow sx={tableRowSize}>
                  {listHeader?.map((value) => (
                    <TableCell
                      key={value.key}
                      sx={{
                        color: themes.headLine,
                        fontWeight: "bold",
                        borderBottom: "1px solid #ddd",
                        bgcolor: "#f5f5f5",
                      }}
                      // 1. Fixed Alignment mapping
                      align={value.align || "left"}
                    >
                      {value.name}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredAdvances?.length === 0 ? (
                  <InfoMessage dataLength={advanceData?.data || []} />
                ) : (
                  filteredAdvances
                    ?.slice(
                      page * rowsPerPage,
                      page * rowsPerPage + rowsPerPage,
                    )
                    ?.map((row, index) => (
                      <React.Fragment key={index}>
                        <TableRow
                          sx={{
                            ...tableRowSize,
                            cursor: "pointer",
                            "&:hover": { bgcolor: "#fafafa" },
                          }}
                          onClick={() => handleView(row)}
                        >
                          {listHeader.map((head) => (
                            <TableCell
                              key={head.key}
                              // 1. Maintain alignment consistency with header
                              align={head.align || "left"}
                              sx={{ borderBottom: "1px solid #f0f0f0" }}
                            >
                              {head.key === "expand" ? (
                                <IconButton
                                  size="small"
                                  onClick={(e) => handleToggle(e, index, row)}
                                >
                                  <ArrowRightIcon
                                    sx={{
                                      transition: "0.3s",
                                      transform:
                                        expandedIndex === index
                                          ? "rotate(90deg)"
                                          : "rotate(0deg)",
                                      color:
                                        expandedIndex === index
                                          ? "#00ab66"
                                          : "inherit",
                                    }}
                                  />
                                </IconButton>
                              ) : head.key === "status" ? (
                                <Box
                                  sx={{
                                    display: "flex",
                                    justifyContent: "flex-start",
                                  }}
                                >
                                  <StatusBadge
                                    color={row?.color_code}
                                    label={row?.status || "N/A"}
                                  />
                                </Box>
                              ) : head.key === "user_name" ? (
                                <Box
                                  sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 1,
                                  }}
                                >
                                  <Avatar
                                    src={row?.image_url}
                                    alt={row?.user_name
                                      ?.charAt(0)
                                      ?.toUpperCase()}
                                    sx={{
                                      width: 32,
                                      height: 32,
                                      fontSize: "0.875rem",
                                      fontWeight: "bold",
                                      bgcolor: row?.image_url
                                        ? "transparent"
                                        : "#1976d2",
                                    }}
                                  >
                                    {!row?.image_url &&
                                      row?.user_name?.charAt(0)?.toUpperCase()}
                                  </Avatar>
                                  <Typography variant="body2">
                                    {row[head.key] ?? "-"}
                                  </Typography>
                                </Box>
                              ) : head.key === "disbursement_date" ? ( // 2. Handle Disbursement Date specifically
                                <Typography variant="body2">
                                  {row[head.key] ? row[head.key] : "N/A"}
                                </Typography>
                              ) : (
                                <Typography variant="body2">
                                  {row[head.key] ?? "-"}
                                </Typography>
                              )}
                            </TableCell>
                          ))}
                        </TableRow>

                        {expandedIndex === index && (
                          <TableRow>
                            <TableCell
                              colSpan={listHeader.length}
                              sx={{ p: 0, borderBottom: "1px solid #eee" }}
                            >
                              <Box
                                sx={{ pl: 6, pr: 2, py: 2, bgcolor: "#fafafa" }}
                              >
                                {/* 3. Switched to new Schedule API Table */}
                                <ExpandedScheduleTable
                                  domain={domain}
                                  advanceId={
                                    expandedData?.id || expandedData?.advance_id
                                  } // Provide correct ID key here
                                />
                              </Box>
                            </TableCell>
                          </TableRow>
                        )}
                      </React.Fragment>
                    ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <Box sx={{ mt: 1 }}>
            {filteredAdvances?.length > 25 && (
              <TablePagination
                className="custom-pagination"
                component="div"
                rowsPerPageOptions={[25, 50, 75, 100]}
                count={filteredAdvances?.length}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
              />
            )}
          </Box>
        </Grid>
      </Paper>

      <Drawer
        anchor="right"
        open={isPanelOpen}
        onClose={(event, reason) => {
          if (reason === "backdropClick" || reason === "escapeKeyDown") return;
          handlePanelClose();
        }}
        sx={drawerStyle}
      >
        {isPanelOpen && (
          <TeamAdvanceSalaryPanel
            mode={panelMode}
            initialData={selectedAdvance}
            onClose={handlePanelClose}
            onAction={handleAction}
            reCallApi={reCallApi}
          />
        )}
      </Drawer>
    </Box>
  );
};

export const TeamAdvanceSalary = () => {
  const { domain, userDetails, userScreenAccess } = useSelector(
    (state) => state.tokenReducer,
  );
  const advModule = userScreenAccess?.module_details?.find(
    (mod) => mod.module_name === "Approve",
  );
  const subModuleId = advModule?.submodules?.find(
    (sub) => sub.sub_module_name === "Advance Salary",
  )?.sub_module_id;

  return (
    <FilterProvider
      domain={domain}
      userDetails={userDetails}
      subModuleId={subModuleId}
    >
      <TeamAdvanceSalaryContent />
    </FilterProvider>
  );
};
